import React, { useEffect, useState, useContext } from "react";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { GraphContext } from "../../../context/GraphContext";
import { DashboardContext } from "../../../context/DashboardContext";
import { GeneralContext } from "../../../context/GeneralContext";
import HtmlTooltip from "../../commonComponents/HtmlTooltip";
import "./fit.scss";
import {
  processSymbols,
  toScientificNotation,
  getShortName,
  filterCurveDataToRange,
  segmentsIntoSingleCurve,
  splitCurveIntoSegmentsByEdges,
  convertDataFromUnitsToUnits,
  isInverseUnit,
  anyUnitToCm1,
  cm1ToAnyUnit,
} from "../../../utils/helpers";
import units from "../../../utils/units";

function X2Chip(props) {
  const {
    fileName,
    fileId,
    modelName,
    quantityName,
    quantityUnit,
    displayUnit,
    weightInitial,
    useFullRangeInitial,
    rangeMinInitial,
    rangeMaxInitial,
    index,
    handleDeleteChip,
    handleTermWeightChange,
    handleDisplayUnitChange,
    handleRangeChange,
    handleClick,
    id,
    chi2 = null,
    activeParams = null,
    nDataPoints = null,
    needsUpdate = false,
  } = props;
  const { selectedChi2Terms } = useContext(GraphContext);
  const { uploadedFiles } = useContext(DashboardContext);
  const { recordedErrorLog } = useContext(GeneralContext);
  const [weight, setWeight] = useState(weightInitial);
  const [isExpanded, setIsExpanded] = useState(!useFullRangeInitial);
  const [useFullRange, setUseFullRange] = useState(useFullRangeInitial);
  const [xMin, setXMin] = useState(rangeMinInitial);
  const [xMax, setXMax] = useState(rangeMaxInitial);
  const [xMinError, setXMinError] = useState(false);
  const [xMaxError, setXMaxError] = useState(false);
  const isInitialMount = React.useRef(true);

  // Get the default unit from the model

  const [selectedUnit, setSelectedUnit] = useState(displayUnit || quantityUnit || units[0]);

  const handleWeightChange = (e) => {
    try {
      if (e.target.value !== undefined && e.target.value !== null) {
        if (e.target.value !== "") {
          const parsedValue = parseFloat(e.target.value);
          if (parsedValue >= 0) {
            setWeight(parsedValue);
          } else {
            setWeight(0);
          }
        } else {
          setWeight(e.target.value);
        }
      }
    } catch (error) {
      recordedErrorLog("Weight change handler failure: ", error);
    }
  };

  const handleBlur = () => {
    if (weight === "") {
      setWeight(0);
    }
  };

  useEffect(() => {
    if (weight === "") {
      handleTermWeightChange(index, 0);
    } else {
      handleTermWeightChange(index, weight);
    }
  }, [weight]);

  useEffect(() => {
    let xMinErrorTemp = false;
    let xMaxErrorTemp = false;
    if (!useFullRange) {
      if (xMin !== "" && isNaN(parseFloat(xMin))) {
        xMinErrorTemp = true;
      }
      if (xMax !== "" && isNaN(parseFloat(xMax))) {
        xMaxErrorTemp = true;
      }
      if (!xMinErrorTemp && !xMaxErrorTemp && xMin !== "" && xMax !== "") {
        if (parseFloat(xMin) >= parseFloat(xMax)) {
          xMinErrorTemp = true;
          xMaxErrorTemp = true;
        }
      }
    }
    if (!xMinErrorTemp && !xMaxErrorTemp) {
      // Skip calling handleRangeChange on initial mount to avoid duplicate chi2 requests
      // The useEffect on selectedChi2Terms in FitOptions will handle the initial request
      if (!isInitialMount.current) {
        handleRangeChange(index, useFullRange, xMin, xMax);
      }
    }
    if (xMinErrorTemp !== xMinError) {
      setXMinError(xMinErrorTemp);
    }
    if (xMaxErrorTemp !== xMaxError) {
      setXMaxError(xMaxErrorTemp);
    }
    // Mark that initial mount is complete
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [useFullRange, xMin, xMax]);

  const handleUnitChange = (e) => {
    e.stopPropagation();
    let newUnit = units.find((unit) => unit.value === e.target.value);
    if (newUnit) {
      setSelectedUnit(newUnit);
      handleDisplayUnitChange(index, newUnit);
    } 
    else {
      setSelectedUnit(quantityUnit || units[0]);
      handleDisplayUnitChange(index, quantityUnit || units[0]);
    }
  };

  function sanitizeChi2(val) {
    try {
      if (typeof val === "number") {
        return toScientificNotation(parseFloat(val), 1);
      } else {
        return "N/A";
      }
    } catch (error) {
      recordedErrorLog("Chi2 sanitization has failed: ", error);
    }
  }

  function sanitizeActiveParams(val) {
    try {
      if (typeof val === "number") {
        return parseInt(val).toString();
        // return toScientificNotation(parseInt(val),1);
      } else {
        return "N/A";
      }
    } catch (error) {
      recordedErrorLog("Active params sanitization has failed: ", error);
    }
  }

  const handleExpandClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleUseFullRangeChange = (e) => {
    e.stopPropagation();
    const isChecked = e.target.checked;
    setUseFullRange(isChecked);

    if (isChecked) {
      // Clear xMin and xMax values when Full Range is checked
      setXMin("");
      setXMax("");
      setSelectedUnit(quantityUnit || units[0]);
    } else {
      // Fill in min and max values from dataset when Full Range is unchecked
      try {
        // Find the file data directly from uploadedFiles using fileName
        const fileData = uploadedFiles.find((file) => file.ID === fileId);
        if (fileData && fileData.dataPoints && fileData.dataPoints.length > 0) {
          let data = fileData.dataPoints;
          data = filterCurveDataToRange(
            data,
            fileData.dataRangeMin,
            fileData.dataRangeMax
          );
          data = segmentsIntoSingleCurve(
            splitCurveIntoSegmentsByEdges(data, fileData.edges)
          );
          let convertedData = convertDataFromUnitsToUnits(
            units[0],
            quantityUnit,
            data
          );

          const xValues = convertedData
            .map((point) => point.x)
            .filter((x) => !isNaN(x));
          if (xValues.length > 0) {
            const minX = Math.min(...xValues);
            const maxX = Math.max(...xValues);
            setXMin(minX.toString());
            setXMax(maxX.toString());
          }
          setSelectedUnit(quantityUnit || units[0]);
        }
      } catch (error) {
        recordedErrorLog("Error getting dataset range: ", error);
      }
    }
  };

  const handleXMinChange = (e) => {
      let parsedValue = parseFloat(e.target.value);
      if (!isNaN(parsedValue)) {
        if (parsedValue === 0) {
          setXMin("0");
        }
        else {
          setXMin(cm1ToAnyUnit(quantityUnit || units[0], anyUnitToCm1(selectedUnit, parsedValue)).toString());
        }
      } 
      else {
        setXMin(e.target.value);
      }
    }

  const handleXMaxChange = (e) => {
    let parsedValue = parseFloat(e.target.value);
    if (!isNaN(parsedValue)) {
      if (parsedValue === 0) {
        setXMax("0");
      }
      else {
        setXMax(cm1ToAnyUnit(quantityUnit || units[0], anyUnitToCm1(selectedUnit, parsedValue)).toString());
      }
    } 
    else {
      setXMax(e.target.value);
    }
  };

  function convertToDisplayUnit(value) {
    let parsedValue = parseFloat(value);
    if (!isNaN(parsedValue)) {
      if (parsedValue === 0) {
        return "0";
      }
      else {
        return cm1ToAnyUnit(selectedUnit, anyUnitToCm1(quantityUnit || units[0], parsedValue)).toString();
      }
    }
    return value;
  }

  return (
    <div
      className={`x2Chip ${
        selectedChi2Terms.some((listId) => listId === id) ? "" : "notSelected"
      }`}
      onClick={(e) => handleClick(e, id)}
      id="chi2-term-chip"
    >
      <div className="chipMainContent">
        <div className="namesContainer">
          <div className="name">
            {fileName.length > 27 ? (
              <HtmlTooltip
                title={
                  <React.Fragment>
                    <div className="customTooltip">{fileName}</div>
                  </React.Fragment>
                }
              >
                <span>{getShortName(fileName, 27)}</span>
              </HtmlTooltip>
            ) : (
              fileName
            )}
          </div>
          <div className="modelAndQuantity">
            <div className="name">{modelName}</div>
            <div className="spacer" />
            <div className="quantityName">{processSymbols(quantityName)}</div>
          </div>
        </div>
        <div className="entriesContainer">
          <TextField
            style={{
              height: "100%",
              width: "67px",
            }}
            label="Weight"
            value={weight}
            onChange={handleWeightChange}
            onBlur={handleBlur}
            className="rangeVal"
            size="small"
            type="number"
          />
          <div className="chiValues">
            <div className="valueEntry">
              <div className="title">
                Χ<sup>2</sup>:
              </div>
              <div
                className={`value${needsUpdate ? " greyOut" : ""}`}
                id="calculated-chi2-value"
              >
                {chi2 === null ? "N/A" : sanitizeChi2(chi2)}
              </div>
            </div>
            <div className="valueEntry">
              <div className="title" title="Number of Active Fit Parameters">
                FP:
              </div>
              <div
                className={`value${needsUpdate ? " greyOut" : ""}`}
                id="active-parameters-value"
              >
                {activeParams === null
                  ? "N/A"
                  : sanitizeActiveParams(activeParams)}
              </div>
            </div>
          </div>
        </div>
        <div className="deleteArea">
          <div className="deleteSection">
            <HighlightOffIcon
              className="deleteIcon"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteChip(index);
              }}
            />
            <div
              className="deleteOverlay"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteChip(index);
              }}
            />
          </div>
          <div className="expandSection">
            {isExpanded ? (
              <RemoveIcon
                className="expandIcon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpandClick(e);
                }}
              />
            ) : (
              <AddIcon
                className="expandIcon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpandClick(e);
                }}
              />
            )}
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="expandedContent">
          <div className="rangeInputs">
            <FormControlLabel
              control={
                <Checkbox
                  checked={useFullRange}
                  onChange={handleUseFullRangeChange}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
              }
              label={useFullRange ? "Full Range" : "FR"}
              className="fullRangeCheckbox"
              title="Use Full Range"
            />
            {!useFullRange && (
              <>
                <div className="rangeInput">
                  <TextField
                    size="small"
                    type="number"
                    value={convertToDisplayUnit(xMin)}
                    onChange={handleXMinChange}
                    className="rangeTextField"
                    error={xMinError}
                  />
                </div>
                <div className="rangeInput">
                  <div className="inputLabel">to</div>
                  <TextField
                    size="small"
                    type="number"
                    value={convertToDisplayUnit(xMax)}
                    onChange={handleXMaxChange}
                    className="rangeTextField"
                    error={xMaxError}
                  />
                </div>
                <div className="rangeInput">
                  <FormControl size="small" className="unitSelect">
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={selectedUnit.value}
                      onChange={handleUnitChange}
                      onClick={(e) => e.stopPropagation()}
                      label="Unit"
                    >
                      {(() => {
                        const filteredUnits = units.filter((unit) => {
                          return (
                            isInverseUnit(unit) === isInverseUnit(quantityUnit||units[0])
                          );
                        });

                        return filteredUnits.map((unit) => (
                          <MenuItem key={unit.value} value={unit.value}>
                            {typeof unit.name === "string" ? (
                              unit.name
                            ) : (
                              <span>{unit.name}</span>
                            )}
                          </MenuItem>
                        ));
                      })()}
                    </Select>
                  </FormControl>
                </div>
              </>
            )}
            <div className="rangeInput dpContainer">
              <div className="inputLabel" title="Number of Data Points">
                DP:
              </div>
              <div
                className={`value${needsUpdate ? " greyOut" : ""}`}
                id="active-parameters-value"
              >
                {nDataPoints === null
                  ? "N/A"
                  : sanitizeActiveParams(nDataPoints)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default X2Chip;
